/**
 * ParameterRepository with S3 + DynamoDB Hybrid Caching
 *
 * Domain-agnostic, data-driven provider for UI dictionaries, feature flags,
 * and workflow schemas from backend tenant configuration.
 *
 * Architecture:
 * - DynamoDB stores metadata (versions, checksums, S3 keys)
 * - S3 stores actual parameter content
 * - Frontend caches with version validation
 * - Apollo Client integration for APQ, cache, and error normalization
 */

import { ApolloClient, gql } from '@apollo/client';

export type DictionaryItem = { value: string; label: string; color?: string };

export interface WorkflowSchema {
  id: string;
  name: string;
  domain: string;
  primitives: Array<{
    type: string;
    id: string;
    properties?: Record<string, any>;
    constraints?: any[];
    relationships?: any[];
  }>;
  layout?: any;
  validation?: any;
  safety?: any;
}

interface ParameterMetadata {
  s3_bucket: string;
  s3_key: string;
  version: string;
  checksum: string;
  size_bytes: number;
  updated_at: string;
  content_type: string;
  cache_ttl: number;
}

interface CachedParameter {
  data: any;
  version: string;
  checksum: string;
  cached_at: string;
  expires_at: string;
}

export class ParameterRepository {
  private apolloClient: ApolloClient | null = null;

  /**
   * Configure the repository with an Apollo Client instance.
   * When configured, GraphQL queries use Apollo Client (APQ, cache, error normalization).
   * When not configured, falls back to raw fetch().
   */
  configure(apolloClient: ApolloClient): void {
    this.apolloClient = apolloClient;
  }

  /**
   * Execute a GraphQL query via Apollo Client (preferred) or raw fetch (fallback).
   * Centralizes auth headers, tenant context, and error handling.
   */
  private async graphqlQuery(
    query: string,
    variables: Record<string, unknown>,
    tenantId?: string,
  ): Promise<{ data: any; errors?: any[] }> {
    const resolvedTenant = this.resolveActiveTenantId(tenantId);

    if (this.apolloClient) {
      const result = await this.apolloClient.query({
        query: gql(query),
        variables,
        context: {
          headers: { 'x-tenant': resolvedTenant },
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        return { data: result.data, errors: [result.error] };
      }
      return { data: result.data };
    }

    // Fallback: raw fetch (no APQ, no Apollo cache)
    const response = await fetch(this.getGraphqlUrl(tenantId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getAuthToken()}`,
        'x-tenant': resolvedTenant,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Stub mode parameter provider - bypasses S3/DynamoDB for development
   * Returns domain-agnostic data based on FERP's 16 primitive types
   */
  private async getParameterStubMode(tenantId: string, paramType: string, paramName: string): Promise<any> {
    console.log(`Providing stub data for ${paramType}.${paramName} in tenant ${tenantId}`);

    if (paramType === 'workflow' && paramName === 'templates') {
      // Return domain-agnostic workflow templates using FERP 16 primitive types
      return [
        {
          id: 'timesheet-basic',
          name: 'Basic Timesheet',
          domain: 'role-0-0-2',
          type: 'timesheet',
          description: 'Domain-agnostic timesheet using FERP primitive types',
          primitives: [
            { type: 'TextField', id: 'employee_name', label: 'Employee Name', required: true },
            { type: 'DateField', id: 'work_date', label: 'Work Date', required: true },
            { type: 'NumberField', id: 'hours_worked', label: 'Hours Worked', required: true, min: 0, max: 24, step: 0.25 },
            { type: 'SelectField', id: 'activity_type', label: 'Activity Type', required: true,
              options: [
                { value: 'direct_care', label: 'Direct Care' },
                { value: 'documentation', label: 'Documentation' },
                { value: 'training', label: 'Training' }
              ]
            }
          ],
          layout: {
            type: 'form',
            sections: [
              { id: 'basic_info', title: 'Basic Information', primitives: ['employee_name', 'work_date'] },
              { id: 'time_details', title: 'Time Details', primitives: ['hours_worked', 'activity_type'] }
            ]
          }
        },
        {
          id: 'timesheet-pca',
          name: 'PCA Timesheet',
          domain: 'role-0-0-2',
          type: 'timesheet',
          description: 'Personal Care Assistant timesheet using primitive types',
          primitives: [
            { type: 'TextField', id: 'pca_name', label: 'PCA Name', required: true },
            { type: 'TextField', id: 'client_name', label: 'Client Name', required: true },
            { type: 'DateField', id: 'service_date', label: 'Service Date', required: true },
            { type: 'TimeField', id: 'start_time', label: 'Start Time', required: true },
            { type: 'TimeField', id: 'end_time', label: 'End Time', required: true },
            { type: 'MultiSelectField', id: 'services_provided', label: 'Services Provided', required: true,
              options: [
                { value: 'personal_care', label: 'Personal Care' },
                { value: 'medication_assistance', label: 'Medication Assistance' },
                { value: 'mobility_support', label: 'Mobility Support' },
                { value: 'companionship', label: 'Companionship' }
              ]
            }
          ],
          layout: {
            type: 'form',
            sections: [
              { id: 'provider_info', title: 'Provider Information', primitives: ['pca_name', 'client_name'] },
              { id: 'service_info', title: 'Service Information', primitives: ['service_date', 'start_time', 'end_time', 'services_provided'] }
            ]
          }
        },
        {
          id: 'invoice-processing',
          name: 'Invoice Processing',
          domain: 'financial',
          type: 'workflow',
          description: 'Invoice processing workflow using primitive types',
          primitives: [
            { type: 'TextField', id: 'invoice_number', label: 'Invoice Number', required: true },
            { type: 'NumberField', id: 'amount', label: 'Amount', required: true, min: 0, step: 0.01 },
            { type: 'DateField', id: 'due_date', label: 'Due Date', required: true },
            { type: 'FileField', id: 'invoice_document', label: 'Invoice Document', required: true, accept: '.pdf,.jpg,.png' },
            { type: 'SelectField', id: 'status', label: 'Status', required: true,
              options: [
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' }
              ]
            }
          ],
          layout: {
            type: 'form',
            sections: [
              { id: 'invoice_info', title: 'Invoice Information', primitives: ['invoice_number', 'amount', 'due_date'] },
              { id: 'documents', title: 'Documents & Status', primitives: ['invoice_document', 'status'] }
            ]
          }
        },
        {
          id: 'case-intake',
          name: 'Legal Case Intake',
          domain: 'legal',
          type: 'workflow',
          description: 'Legal case intake using primitive types',
          primitives: [
            { type: 'TextField', id: 'client_name', label: 'Client Name', required: true },
            { type: 'TextField', id: 'case_type', label: 'Case Type', required: true },
            { type: 'DateField', id: 'incident_date', label: 'Incident Date', required: true },
            { type: 'TextAreaField', id: 'case_summary', label: 'Case Summary', required: true, rows: 5 },
            { type: 'BooleanField', id: 'privilege_claimed', label: 'Attorney-Client Privilege Claimed', required: true }
          ],
          layout: {
            type: 'form',
            sections: [
              { id: 'case_details', title: 'Case Details', primitives: ['client_name', 'case_type', 'incident_date'] },
              { id: 'case_info', title: 'Case Information', primitives: ['case_summary', 'privilege_claimed'] }
            ]
          }
        }
      ];
    }

    if (paramType === 'schema') {
      // Return schema based on paramName (which is the template ID)
      const templates = await this.getParameterStubMode(tenantId, 'workflow', 'templates');
      const template = templates.find((t: any) => t.id === paramName);

      if (template) {
        // Convert template to schema format
        return {
          id: template.id,
          name: template.name,
          domain: template.domain,
          primitives: template.primitives,
          layout: template.layout,
          validation: template.validation,
          safety: template.safety
        };
      }
      return null;
    }

    if (paramType === 'dictionaries') {
      // ⚠️ STUB MODE ONLY - Must come from backend
      console.warn('STUB MODE: Dictionary data must come from backend in production');
      return [];
    }

    if (paramType === 'features') {
      return {
        enable_advanced_forms: true,
        enable_safety_monitoring: true,
        enable_audit_trails: true,
        max_concurrent_workflows: 10
      };
    }

    if (paramType === 'app' && paramName === 'config') {
      return {
        currentTenantId: tenantId,
        currentDomain: 'role-0-0-2',
        version: '1.0.0',
        supportedPrimitives: [
          'TextField', 'DateField', 'TimeField', 'NumberField', 'SelectField',
          'MultiSelectField', 'BooleanField', 'TextAreaField', 'FileField',
          'RangeField', 'ColorField', 'EmailField', 'UrlField', 'PhoneField',
          'CurrencyField', 'PasswordField'
        ]
      };
    }

    // ✅ MEDICAL DATA: Defer to backend GraphQL API - no frontend medical data storage
    if (paramType === 'medical') {
      console.warn(`Medical data access for ${paramName} should use backend GraphQL API, not frontend parameters`);
      return null; // Force backend usage
    }

    if (paramType === 'compliance' && paramName === 'field-classifications') {
      // ✅ DATA-DRIVEN: Regulatory field classifications from metadata
      return {
        classifications: {
          phi: [
            'ssn', 'medical_record_number', 'health_plan_id', 'patient_id',
            'date_of_birth', 'phone_number', 'email_address', 'address'
          ],
          pci: [
            'card_number', 'cvv', 'expiry_date', 'cardholder_name',
            'billing_address', 'bank_account_number', 'routing_number'
          ],
          sensitive: [
            'diagnosis_code', 'medication_list', 'allergy_information',
            'treatment_notes', 'family_history', 'genetic_information'
          ],
          financial: [
            'salary', 'insurance_policy_number', 'claim_amount',
            'payment_amount', 'account_balance', 'credit_score'
          ]
        },
        metadata: {
          version: '1.0.0',
          lastUpdated: new Date().toISOString(),
          complianceFrameworks: ['HIPAA', 'PCI-DSS', 'SOX', 'GDPR'],
          tenantId: tenantId
        }
      };
    }

    if (paramType === 'compliance' && paramName === 'medical-code-policies') {
      // ✅ DATA-DRIVEN: Medical code validation policies
      return {
        rules: {
          CPT: {
            // ✅ DATA-DRIVEN: All medical business rules must come from backend tenant configuration
            deprecatedCodes: [], // Backend-driven: tenant configures deprecated codes
            preauthRequired: [], // Backend-driven: tenant configures preauth requirements
            manualReviewRequired: [], // Backend-driven: tenant configures review requirements
            formatValidation: '^\\d{5}$' // Technical validation only - not medical business logic
          },
          ICD10: {
            // ✅ DATA-DRIVEN: All medical business rules must come from backend tenant configuration
            deprecatedCodes: [], // Backend-driven: tenant configures deprecated codes
            preauthRequired: [], // Backend-driven: tenant configures preauth requirements
            manualReviewRequired: [], // Backend-driven: tenant configures review requirements
            formatValidation: '^[A-Z]\\d{2}(\\.\\d{1,3})?$' // Technical validation only - not medical business logic
          }
        },
        metadata: {
          version: '1.0.0',
          lastUpdated: new Date().toISOString(),
          tenantId: tenantId,
          complianceLevel: 'HIPAA'
        }
      };
    }

    // ✅ DATA-DRIVEN: Safety thresholds MUST come from backend - no frontend JSON files
    if (paramType === 'safety' && paramName === 'thresholds') {
      console.warn('[LEGAL COMPLIANCE] Safety thresholds must come from backend SafetyThresholdService - no frontend JSON files allowed');
      // Delegate to SafetyThresholdService for all safety threshold access
      return Promise.resolve({
        note: 'Use SafetyThresholdService for backend-driven safety thresholds',
        backend_url: '/graphql',
        legal_compliance: 'Tenants control all safety thresholds through backend configuration'
      });
    }

    if (paramType === 'system' && paramName === 'cache-config') {
      // ✅ DATA-DRIVEN: Cache configuration per tenant
      return {
        defaultCacheTtl: 300000, // 5 minutes
        emergencyModeTtl: 30000, // 30 seconds
        maxCacheSize: 50 * 1024 * 1024, // 50MB
        compressionEnabled: true,
        encryptionRequired: true,
        metadata: {
          version: '1.0.0',
          tenantId: tenantId
        }
      };
    }

    if (paramType === 'system' && paramName === 'timeouts') {
      // ✅ DATA-DRIVEN: Operation timeouts per tenant
      return {
        default: 30000, // 30 seconds
        operations: {
          parameter_fetch: 10000, // 10 seconds
          medical_code_lookup: 5000, // 5 seconds
          safety_validation: 2000, // 2 seconds
          emergency_override: 1000, // 1 second
          audit_logging: 15000, // 15 seconds
          workflow_validation: 8000 // 8 seconds
        },
        metadata: {
          version: '1.0.0',
          tenantId: tenantId,
          lifeCritical: true
        }
      };
    }

    if (paramType === 'config' && paramName === 'session') {
      // ⚠️ STUB MODE ONLY - In production, this MUST come from backend GraphQL/DynamoDB
      // This is a development-only fallback when backend is not available
      console.warn('STUB MODE: Returning minimal session config - production MUST use backend configuration');
      return {
        durations: {
          default: 3600000, // 1 hour - SAFETY DEFAULT ONLY
          byDomain: {},      // MUST come from backend tenant configuration
          byRole: {}         // MUST come from backend tenant configuration
        },
        metadata: {
          stub_mode: true,
          warning: 'This is stub data - production must fetch from backend'
        }
      };
    }

    return null;
  }

  /**
   * Get cached parameter from localStorage
   */
  private getCachedParameter(
    tenantId: string,
    paramType: string,
    paramName: string,
  ): CachedParameter | null {
    try {
      const cacheKey = `ferp_param_${tenantId}_${paramType}_${paramName}`;
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        return null;
      }

      const cachedData: CachedParameter = JSON.parse(cached);

      // Check if cache is expired
      if (new Date() > new Date(cachedData.expires_at)) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return cachedData;
    } catch (error) {
      console.error('Error reading cached parameter:', error);
      return null;
    }
  }

  /**
   * Set cached parameter in localStorage
   */
  private setCachedParameter(
    tenantId: string,
    paramType: string,
    paramName: string,
    cachedData: CachedParameter,
  ): void {
    try {
      const cacheKey = `ferp_param_${tenantId}_${paramType}_${paramName}`;
      localStorage.setItem(cacheKey, JSON.stringify(cachedData));
    } catch (error) {
      console.error('Error caching parameter:', error);
    }
  }

  /**
   * Get parameter metadata from DynamoDB via GraphQL
   */
  private async getParameterMetadata(
    tenantId: string,
    paramType: string,
    paramName: string,
  ): Promise<ParameterMetadata | null> {
    try {
      const query = `
        query GetParameterMetadata($tenant: String!, $paramType: String!, $paramName: String!) {
          getParameterMetadata(tenant: $tenant, paramType: $paramType, paramName: $paramName) {
            s3Bucket
            s3Key
            version
            checksum
            sizeBytes
            updatedAt
            contentType
            cacheTtl
          }
        }
      `;

      const variables = { tenant: tenantId, paramType, paramName };
      const result = await this.graphqlQuery(query, variables, tenantId);

      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        return null;
      }

      const metadata = result.data?.getParameterMetadata;
      if (!metadata) {
        return null;
      }

      return {
        s3_bucket: metadata.s3Bucket,
        s3_key: metadata.s3Key,
        version: metadata.version,
        checksum: metadata.checksum,
        size_bytes: metadata.sizeBytes,
        updated_at: metadata.updatedAt,
        content_type: metadata.contentType,
        cache_ttl: metadata.cacheTtl,
      };
    } catch (error) {
      console.error('Failed to fetch parameter metadata:', error);
      return null;
    }
  }

  /**
   * Fetch parameter content from S3 via signed URL
   */
  private async fetchFromS3(bucket: string, key: string): Promise<any> {
    try {
      // Step 1: Get signed URL via GraphQL
      const query = `
        query GenerateS3SignedUrl($bucket: String!, $key: String!, $expirySeconds: Int) {
          generateS3SignedUrl(bucket: $bucket, key: $key, expirySeconds: $expirySeconds)
        }
      `;

      const variables = { bucket, key, expirySeconds: 900 }; // 15 minutes
      const tenantId = this.resolveActiveTenantId();
      const result = await this.graphqlQuery(query, variables, tenantId);

      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error('Failed to generate signed URL');
      }

      const signedUrl = result.data?.generateS3SignedUrl;
      if (!signedUrl) {
        throw new Error('No signed URL returned');
      }

      // Step 2: Fetch parameter content from S3 using signed URL
      const s3Response = await fetch(signedUrl);

      if (!s3Response.ok) {
        throw new Error(`S3 fetch failed! status: ${s3Response.status}`);
      }

      const contentType = s3Response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await s3Response.json();
      } else {
        return await s3Response.text();
      }
    } catch (error) {
      console.error('Failed to fetch from S3:', error);

      // LIFE-CRITICAL: No fallback data - system must work with proper auth and cache validation
      // Log audit trail for parameter access failure
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(`Parameter access failed for S3 key: ${key}`, {
        bucket,
        key,
        timestamp: new Date().toISOString(),
        error: errorMessage
      });

      throw error;
    }
  }

  /**
   * Generic parameter fetch with version-based caching and Authentik auth validation
   * LIFE-CRITICAL: Enforces authentication and hash validation before returning data
   */
  private async getParameter(tenantId: string, paramType: string, paramName: string): Promise<any> {
    // 1. Check if we're in stub/development mode (backend running without Redis or using localhost)
    const authToken = this.getAuthToken();
    const graphqlUrl = this.getGraphqlUrl(tenantId);
    const isStubMode = authToken.includes('stub') ||
                       authToken.includes('development') ||
                       authToken.includes('simulated-signature') ||
                       graphqlUrl.includes('localhost') ||
                       graphqlUrl.includes('127.0.0.1') ||
                       !graphqlUrl.includes('production');

    if (isStubMode) {
      console.log(`Stub mode detected - bypassing S3/DynamoDB for ${paramType}.${paramName}`);
      return this.getParameterStubMode(tenantId, paramType, paramName);
    }

    // 2. Validate Authentik authentication and extract hash keys
    const authResult = this.validateAuthentikAuth(tenantId);
    if (!authResult.isValid) {
      throw new Error(`Authentication failed for tenant ${tenantId} - cannot access parameters`);
    }

    // 3. Check localStorage cache
    const cached = this.getCachedParameter(tenantId, paramType, paramName);

    // 4. Query DynamoDB for version/metadata
    const metadata = await this.getParameterMetadata(tenantId, paramType, paramName);

    if (!metadata) {
      // No metadata found - check if we have valid cached data
      if (cached?.data) {
        console.warn(`No metadata found for ${paramType}.${paramName}, using cached data`);
        return cached.data;
      }
      throw new Error(`Parameter ${paramType}.${paramName} not found for tenant ${tenantId}`);
    }

    // 4. Version comparison with hash validation
    if (cached && cached.version === metadata.version && cached.checksum === metadata.checksum) {
      // Validate cached data hash against Authentik-provided keys
      const isValidHash = await this.validateS3ContentHash(
        cached.data,
        metadata.checksum,
        authResult.hashKeys
      );

      if (isValidHash) {
        console.log(`✅ Cache hit with valid hash for ${paramType}.${paramName}`);
        return cached.data; // Cache hit with valid hash!
      } else {
        console.warn(`Cache hit but hash validation failed for ${paramType}.${paramName} - refetching`);
        // Clear invalid cache
        const cacheKey = `ferp_param_${tenantId}_${paramType}_${paramName}`;
        localStorage.removeItem(cacheKey);
      }
    }

    // 5. Cache miss or invalid hash - fetch from S3
    let s3Data: any;
    try {
      s3Data = await this.fetchFromS3(metadata.s3_bucket, metadata.s3_key);
    } catch (s3Error) {
      console.error(`S3 fetch failed for ${paramType}.${paramName}:`, s3Error);

      // If we have cached data as last resort, validate it first
      if (cached?.data) {
        const isCachedValid = await this.validateS3ContentHash(
          cached.data,
          cached.checksum,
          authResult.hashKeys
        );

        if (isCachedValid) {
          console.warn(`Using cached data as fallback for ${paramType}.${paramName}`);
          return cached.data;
        }
      }

      throw new Error(`Failed to fetch parameter ${paramType}.${paramName} and no valid cache available`);
    }

    // 6. Validate S3 content hash before caching
    const isValidS3Hash = await this.validateS3ContentHash(
      s3Data,
      metadata.checksum,
      authResult.hashKeys
    );

    if (!isValidS3Hash) {
      throw new Error(`S3 content hash validation failed for ${paramType}.${paramName} - potential data tampering`);
    }

    // 7. Update cache with validated data
    this.setCachedParameter(tenantId, paramType, paramName, {
      data: s3Data,
      version: metadata.version,
      checksum: metadata.checksum,
      cached_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + metadata.cache_ttl * 1000).toISOString(),
    });

    console.log(`✅ Parameter ${paramType}.${paramName} fetched and validated successfully`);
    return s3Data;
  }

  // Load dictionaries from backend with version-based caching
  async getDictionary(name: string, tenantId?: string): Promise<DictionaryItem[]> {
    if (!tenantId) {
      console.warn('getDictionary: tenantId required for hybrid caching');
      return [];
    }

    try {
      const data = await this.getParameter(tenantId, 'dictionaries', name);
      return data || [];
    } catch (error) {
      console.error('Failed to fetch dictionary from backend:', error);
      return [];
    }
  }

  // Load feature flags from backend with version-based caching
  async getFeatureFlags(tenantId?: string): Promise<Record<string, any>> {
    if (!tenantId) {
      console.warn('getFeatureFlags: tenantId required for hybrid caching');
      return {};
    }

    try {
      const data = await this.getParameter(tenantId, 'features', 'default');
      return data || {};
    } catch (error) {
      console.error('Failed to fetch feature flags from backend:', error);
      return {};
    }
  }

  // Load app-level params with version-based caching
  async getAppParams(tenantId?: string): Promise<Record<string, any>> {
    if (!tenantId) {
      console.warn('getAppParams: tenantId required for hybrid caching');
      return {
        currentTenantId: 'TENANT_FROM_AUTH',
        currentDomain: 'DOMAIN_FROM_BACKEND',
      };
    }

    try {
      const data = await this.getParameter(tenantId, 'app', 'config');
      return (
        data || {
          currentTenantId: tenantId,
          currentDomain: 'DOMAIN_FROM_BACKEND',
        }
      );
    } catch (error) {
      console.error('Failed to resolve current tenant:', error);
      return {};
    }
  }

  // Load workflow schema from backend with version-based caching
  async getSchemaByType(schemaType: string, tenantId?: string): Promise<WorkflowSchema | null> {
    if (!tenantId) {
      console.warn('getSchemaByType: tenantId required for hybrid caching');
      return null;
    }

    try {
      // First try the parameter metadata flow
      const data = await this.getParameter(tenantId, 'schema', schemaType);
      return data as WorkflowSchema | null;
    } catch (error) {
      console.error('Failed to fetch schema from parameter metadata:', error);

      // Fallback: try direct draft access for individual templates
      try {
        console.log('Trying direct draft access for schema:', schemaType);
        const draftQuery = `
          query GetTemplateDraft($input: GetDraftInput!) {
            getDraft(input: $input) {
              draft
            }
          }
        `;
        const draftVars = {
          input: {
            tenant: tenantId,
            kind: 'WorkflowTemplate',
            key: schemaType
          }
        };

        const result = await this.graphqlQuery(draftQuery, draftVars, tenantId);
        if (result.data?.getDraft?.draft) {
          const template = JSON.parse(result.data.getDraft.draft);
          console.log('✅ Successfully loaded schema from template draft:', schemaType);

          // Convert template to schema format if needed
          if (template.primitives && Array.isArray(template.primitives)) {
            const schema: WorkflowSchema = {
              id: template.id || schemaType,
              name: template.name || schemaType,
              domain: template.domain || 'general',
              primitives: template.primitives,
              layout: template.layout,
              validation: template.validation,
              safety: template.safety
            };
            return schema;
          }
        }
      } catch (draftError) {
        console.error('Template draft fallback also failed:', draftError);
      }

      return null;
    }
  }

  // Load workflow template options from backend with version-based caching
  async getWorkflowTemplates(tenantId?: string): Promise<any[]> {
    if (!tenantId) {
      console.warn('getWorkflowTemplates: tenantId required for hybrid caching');
      return [];
    }

    try {
      // First try the parameter metadata flow
      const data = await this.getParameter(tenantId, 'workflow', 'templates');
      const list = Array.isArray(data) ? data : [];
      // Sanitize: remove placeholders and invalid entries
      return list.filter(
        (t) => !!t && !!t.id && t.id !== 'no-templates' && t.id !== 'error-fallback',
      );
    } catch (error) {
      console.error('Failed to fetch workflow templates from parameter metadata:', error);

      // Fallback: try direct draft access for development/stub mode
      try {
        console.log('Trying direct draft access for workflow templates...');
        const templatesQuery = `
          query GetWorkflowTemplatesDraft($input: GetDraftInput!) {
            getDraft(input: $input) {
              draft
            }
          }
        `;
        const templatesVars = {
          input: {
            tenant: tenantId,
            kind: 'WorkflowTemplates',
            key: 'templates'
          }
        };

        const result = await this.graphqlQuery(templatesQuery, templatesVars, tenantId);
        if (result.data?.getDraft?.draft) {
          const templates = JSON.parse(result.data.getDraft.draft);
          console.log('✅ Successfully loaded workflow templates from draft:', templates.length, 'templates');
          return Array.isArray(templates) ? templates : [];
        }
      } catch (draftError) {
        console.error('Draft fallback also failed:', draftError);
      }

      return [];
    }
  }

  /**
   * Batch version check for multiple parameters
   * Used during login/session start for efficient cache validation
   */
  async batchVersionCheck(
    tenantId: string,
    parameters: Array<{ type: string; name: string }>,
  ): Promise<void> {
    try {
      const query = `
        query BatchParameterVersions($tenant: String!, $parameters: [ParameterVersionInput!]!) {
          batchParameterVersions(tenant: $tenant, parameters: $parameters) {
            paramType
            paramName
            version
            checksum
          }
        }
      `;

      const paramInputs = parameters.map((p) => ({
        paramType: p.type,
        paramName: p.name,
      }));

      const variables = { tenant: tenantId, parameters: paramInputs };
      const result = await this.graphqlQuery(query, variables, tenantId);

      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        return;
      }

      const versions = result.data?.batchParameterVersions || [];

      // Compare with cached versions and clear outdated cache
      for (const version of versions) {
        const cached = this.getCachedParameter(tenantId, version.paramType, version.paramName);

        if (
          cached &&
          (cached.version !== version.version || cached.checksum !== version.checksum)
        ) {
          // Cache is outdated, clear it
          const cacheKey = `ferp_param_${tenantId}_${version.paramType}_${version.paramName}`;
          localStorage.removeItem(cacheKey);
          console.log(`Cleared outdated cache for ${version.paramType}.${version.paramName}`);
        }
      }

      console.log(
        `Batch version check completed: ${parameters.length} requested, ${versions.length} found`,
      );
    } catch (error) {
      console.error('Batch version check failed:', error);
    }
  }

  /**
   * Clear cache for a specific tenant (useful for logout/tenant switch)
   */
  clearTenantCache(tenantId: string): void {
    try {
      const keys = Object.keys(localStorage);
      const tenantCacheKeys = keys.filter((key) => key.startsWith(`ferp_param_${tenantId}_`));

      tenantCacheKeys.forEach((key) => {
        localStorage.removeItem(key);
      });

      console.log(`Cleared ${tenantCacheKeys.length} cached parameters for tenant ${tenantId}`);
    } catch (error) {
      console.error('Failed to clear tenant cache:', error);
    }
  }

  /**
   * Get configuration value from backend
   * Used for domain-agnostic configuration like session durations
   */
  async getConfigurationValue(configPath: string, tenantId?: string): Promise<any> {
    if (!tenantId) {
      console.warn('getConfigurationValue: tenantId required for configuration lookup');
      return null;
    }

    try {
      // Configuration values are stored under the 'config' parameter type
      // ConfigPath format: "category.subcategory.key" e.g., "session.durations.default"
      const [category, ...pathParts] = configPath.split('.');
      const configKey = pathParts.join('.');

      if (!category) {
        console.warn(`Invalid config path: ${configPath}`);
        return null;
      }

      // Fetch configuration from backend using existing parameter infrastructure
      const configData = await this.getParameter(tenantId, 'config', category);

      if (!configData) {
        console.warn(`No configuration found for category: ${category}`);
        return null;
      }

      // Navigate through nested configuration structure
      let value = configData;
      for (const part of pathParts) {
        if (value && typeof value === 'object' && part in value) {
          value = value[part];
        } else {
          console.warn(`Configuration key not found: ${configPath}`);
          return null;
        }
      }

      console.log(`✅ Configuration value retrieved for ${configPath}: ${JSON.stringify(value)}`);
      return value;

    } catch (error) {
      console.error(`Failed to fetch configuration value for ${configPath}:`, error);
      return null;
    }
  }

  /**
   * Validate Authentik authentication and extract tenant hash keys
   * LIFE-CRITICAL: Ensures only authenticated users with valid tenant access
   */
  private validateAuthentikAuth(tenantId: string): { isValid: boolean; hashKeys: string[] } {
    try {
      const token = this.getAuthToken();

      if (!token || token.length === 0) {
        console.warn('No authentication token found - cannot access tenant parameters');
        return { isValid: false, hashKeys: [] };
      }

      // Decode JWT token to extract Authentik claims (basic validation)
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3 || !tokenParts[1]) {
          console.error('Invalid JWT token format');
          return { isValid: false, hashKeys: [] };
        }

        const payload = JSON.parse(atob(tokenParts[1]));

        // Validate Authentik issuer (OIDC standard)
        if (!payload.iss || !payload.iss.includes('authentik')) {
          console.warn('Token not from Authentik IdP');
          return { isValid: false, hashKeys: [] };
        }

        // Extract tenant-specific hash keys from Authentik custom claims
        // Authentik uses standard claims (not custom: prefix like Cognito)
        const tenantClaim = payload.tenant || payload.ferp_tenant_slug;
        const hashKeys = payload.ferp_s3_hash_keys || payload.s3_hash_keys || [];

        if (tenantClaim !== tenantId) {
          console.error(`Token tenant (${tenantClaim}) does not match requested tenant (${tenantId})`);
          return { isValid: false, hashKeys: [] };
        }

        console.log(`✅ Authentik auth validated for tenant: ${tenantId}`, {
          subject: payload.sub,
          tenant: tenantClaim,
          hashKeyCount: Array.isArray(hashKeys) ? hashKeys.length : 0
        });

        return {
          isValid: true,
          hashKeys: Array.isArray(hashKeys) ? hashKeys : []
        };

      } catch (decodeError) {
        console.error('Failed to decode JWT token:', decodeError);
        return { isValid: false, hashKeys: [] };
      }

    } catch (error) {
      console.error('Authentik auth validation failed:', error);
      return { isValid: false, hashKeys: [] };
    }
  }

  /**
   * Validate S3 content hash against Authentik-provided hash keys
   * LIFE-CRITICAL: Ensures cache integrity and prevents data tampering
   */
  private async validateS3ContentHash(
    content: any,
    expectedChecksum: string,
    validHashKeys: string[]
  ): Promise<boolean> {
    try {
      if (validHashKeys.length === 0) {
        console.warn('No hash keys available for content validation');
        return false;
      }

      // Generate content hash using the same algorithm as backend
      const contentString = typeof content === 'string' ? content : JSON.stringify(content);
      const encoder = new TextEncoder();
      const data = encoder.encode(contentString);

      // Create SHA-256 hash
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      const computedChecksum = `sha256-${hashHex}`;

      // Validate against expected checksum
      if (computedChecksum !== expectedChecksum) {
        console.error('Content checksum mismatch', {
          expected: expectedChecksum,
          computed: computedChecksum
        });
        return false;
      }

      // Validate against Authentik-provided hash keys
      const isValidKey = validHashKeys.some(key => {
        // In production, this would use HMAC with the key
        // For simulation, check if key contains a hash that matches
        return key.includes(hashHex.substring(0, 16));
      });

      if (!isValidKey) {
        console.error('Content hash not found in valid Authentik hash keys');
        return false;
      }

      console.log('✅ S3 content hash validated successfully');
      return true;

    } catch (error) {
      console.error('S3 content hash validation failed:', error);
      return false;
    }
  }

  /**
   * Get authentication token for GraphQL requests (without Bearer prefix)
   * LIFE-CRITICAL: Single execution path through auth system
   */
  private getAuthToken(): string {
    const candidates: Array<string | null | undefined> = [];

    // Inspect global scopes for tokens that hybrid auth/bootstrap scripts may set
    try {
      const globalScope: any = typeof globalThis !== 'undefined' ? (globalThis as any) : undefined;
      const win: any = typeof window !== 'undefined' ? (window as any) : undefined;

      const globalTokenSources = [
        globalScope?.__FERP_AUTH_TOKEN__,
        globalScope?.__FERP_TOKEN__,
        win?.__AUTH_TOKEN__,
        win?.AUTH_TOKEN,
        win?.__ID_TOKEN__,
        win?.env?.AUTH_TOKEN,
        win?.__ENV__?.AUTH_TOKEN,
        win?.__FERP_BOOTSTRAP__?.authToken,
      ];

      candidates.push(...globalTokenSources);
    } catch (error) {
      console.warn(
        'ParameterRepository.getAuthToken: failed to inspect global scope for tokens',
        error,
      );
    }

    // Inspect storage locations - wrap each access to avoid security errors in restricted environments
    const storageReaders: Array<() => string | null> = [
      () => {
        try {
          return localStorage.getItem('ferp_auth_token');
        } catch {
          return null;
        }
      },
      () => {
        try {
          return sessionStorage.getItem('ferp_auth_token');
        } catch {
          return null;
        }
      },
      () => {
        try {
          return sessionStorage.getItem('auth:token');
        } catch {
          return null;
        }
      },
      () => {
        try {
          return localStorage.getItem('auth:token');
        } catch {
          return null;
        }
      },
      () => {
        try {
          return sessionStorage.getItem('access_token');
        } catch {
          return null;
        }
      },
      () => {
        try {
          return localStorage.getItem('access_token');
        } catch {
          return null;
        }
      },
      () => {
        try {
          return sessionStorage.getItem('id_token');
        } catch {
          return null;
        }
      },
      () => {
        try {
          return localStorage.getItem('id_token');
        } catch {
          return null;
        }
      },
    ];

    for (const reader of storageReaders) {
      const value = reader();
      if (value) {
        candidates.push(value);
      }
    }

    // Cookie fallback (legacy tokens)
    try {
      const cookieToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('ferp_token=') || row.startsWith('ferp_auth_token='));
      if (cookieToken) {
        const [, value] = cookieToken.split('=');
        if (value) {
          candidates.push(decodeURIComponent(value));
        }
      }
    } catch {
      // Ignore cookie parsing failures
    }

    const token = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);

    if (!token) {
      console.warn(
        'No authentication token found for GraphQL requests. Ensure hybrid auth initialization completed successfully.',
      );
      return '';
    }

    const normalized = token.trim().replace(/^Bearer\s+/i, '');
    return normalized;
  }

  /**
   * Resolve the GraphQL endpoint honoring hybrid auth/bootstrap configuration.
   * Falls back to same-origin `/graphql` for legacy local setups.
   */
  private getGraphqlUrl(tenantId?: string): string {
    try {
      const globalScope: any = typeof globalThis !== 'undefined' ? (globalThis as any) : undefined;
      const win: any = typeof window !== 'undefined' ? (window as any) : undefined;

      const fromGlobals =
        globalScope?.__FERP_GRAPHQL_URL__ ||
        globalScope?.__GQL_URL__ ||
        win?.__FERP_GRAPHQL_URL__ ||
        win?.__GQL_URL__ ||
        win?.env?.GQL_URL ||
        win?.env?.GRAPHQL_URL ||
        win?.__ENV__?.GRAPHQL_URL;

      if (typeof fromGlobals === 'string' && fromGlobals.trim().length > 0) {
        return fromGlobals.trim();
      }

      const sessionUrl =
        this.readStorageValue('ferp:graphql_url') ||
        this.readStorageValue('graphql_url') ||
        (tenantId ? this.readStorageValue(`ferp:graphql_url:${tenantId}`) : null) ||
        (tenantId ? this.readStorageValue(`graphql_url:${tenantId}`) : null);
      if (sessionUrl) {
        return sessionUrl;
      }

      const localUrl =
        this.readStorageValue('ferp:graphql_url', 'localStorage') ||
        this.readStorageValue('graphql_url', 'localStorage') ||
        (tenantId ? this.readStorageValue(`ferp:graphql_url:${tenantId}`, 'localStorage') : null) ||
        (tenantId ? this.readStorageValue(`graphql_url:${tenantId}`, 'localStorage') : null);
      if (localUrl) {
        return localUrl;
      }
    } catch (error) {
      console.warn(
        'ParameterRepository.getGraphqlUrl: failed to resolve URL from globals/storage',
        error,
      );
    }

    return '/graphql';
  }

  private readStorageValue(
    key: string,
    storageType: 'sessionStorage' | 'localStorage' = 'sessionStorage',
  ): string | null {
    try {
      if (storageType === 'sessionStorage') {
        return sessionStorage.getItem(key);
      }
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * Derive the active tenant id from runtime globals/storage.
   * Falls back to provided tenantId argument when available.
   */
  private resolveActiveTenantId(fallbackTenantId?: string): string {
    try {
      const globalScope: any = typeof globalThis !== 'undefined' ? (globalThis as any) : undefined;
      const win: any = typeof window !== 'undefined' ? (window as any) : undefined;

      const fromGlobals =
        globalScope?.__FERP_TENANT__ ||
        globalScope?.__TENANT__ ||
        win?.__FERP_TENANT__ ||
        win?.__TENANT__ ||
        win?.env?.TENANT ||
        win?.__ENV__?.TENANT;

      if (fromGlobals && String(fromGlobals).trim().length > 0) {
        return String(fromGlobals).trim();
      }

      try {
        const sessionTenant =
          sessionStorage.getItem('tenant') || sessionStorage.getItem('tenant_id');
        if (sessionTenant) {
          return sessionTenant;
        }
      } catch {
        // ignore storage errors
      }

      try {
        const localTenant = localStorage.getItem('tenant') || localStorage.getItem('tenant_id');
        if (localTenant) {
          return localTenant;
        }
      } catch {
        // ignore storage errors
      }
    } catch (error) {
      console.warn(
        'ParameterRepository.resolveActiveTenantId: failed to resolve tenant from globals/storage',
        error,
      );
    }

    if (fallbackTenantId && fallbackTenantId.trim().length > 0) {
      return fallbackTenantId;
    }

    return 'public';
  }
}

export const parameterRepository = new ParameterRepository();
